import React, { useEffect } from "react";

const TeamDetailStandingsTab = ({
  standings,
  decodedTeamName,
  teamColors,
  teamRowRef,
  loading,
}) => {
  // Auto-scroll to team row on mount (must be before any early returns)
  useEffect(() => {
    if (teamRowRef.current) {
      teamRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [standings, teamRowRef]);

  if (loading) {
    return (
      <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
        <div className="p-3 border-b border-white/[0.06]">
          <div className="flex gap-8">
            <div className="w-6 h-4 bg-white/10 rounded animate-pulse" />
            <div className="w-32 h-4 bg-white/10 rounded animate-pulse" />
            {[...Array(7)].map((_, i) => (
              <div
                className="w-8 h-4 bg-white/10 rounded animate-pulse"
                key={i}
              />
            ))}
          </div>
        </div>
        {[...Array(10)].map((_, i) => (
          <div
            className="flex gap-8 p-3 border-b border-white/[0.04] animate-pulse"
            key={i}
          >
            <div className="w-6 h-4 bg-white/10 rounded" />
            <div className="w-32 h-4 bg-white/10 rounded" />
            {[...Array(7)].map((__, j) => (
              <div className="w-8 h-4 bg-white/5 rounded" key={j} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-20">
        <i className="fas fa-trophy text-5xl text-white/10 mb-4" />
        <p className="text-white/40 text-lg">Standings not available</p>
      </div>
    );
  }

  const matchesTeam = (name) => {
    if (!name) return false;
    const a = name.toLowerCase();
    const b = decodedTeamName.toLowerCase();

    return a === b || a.includes(b) || b.includes(a);
  };

  return (
    <div className="space-y-8">
      {standings.map((group, gi) => {
        if (!Array.isArray(group) || group.length === 0) return null;

        // Group header (for conferences/divisions)
        const groupName =
          group[0]?.group || group[0]?.conference || group[0]?.division;

        return (
          <div key={gi}>
            {standings.length > 1 && groupName && (
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-3">
                {groupName}
              </h3>
            )}
            <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/30 text-xs border-b border-white/[0.06]">
                    <th className="text-left p-3 w-8">#</th>
                    <th className="text-left p-3">Team</th>
                    <th className="text-center p-3 w-10">MP</th>
                    <th className="text-center p-3 w-10">W</th>
                    <th className="text-center p-3 w-10">D</th>
                    <th className="text-center p-3 w-10">L</th>
                    <th className="text-center p-3 w-10 hidden md:table-cell">
                      GF
                    </th>
                    <th className="text-center p-3 w-10 hidden md:table-cell">
                      GA
                    </th>
                    <th className="text-center p-3 w-10 hidden md:table-cell">
                      GD
                    </th>
                    <th className="text-center p-3 w-12">Pts</th>
                  </tr>
                </thead>
                <tbody>
                  {group.map((row, ri) => {
                    const tName = row.team?.name || row.name || "";
                    const isThis = matchesTeam(tName);
                    const rank = row.rank || row.position || ri + 1;
                    const played = row.all?.played ?? row.games?.played ?? "–";
                    const wins =
                      row.all?.win ?? row.games?.win?.total ?? row.wins ?? "–";
                    const draws =
                      row.all?.draw ??
                      row.games?.draw?.total ??
                      row.ties ??
                      "–";
                    const losses =
                      row.all?.lose ??
                      row.games?.lose?.total ??
                      row.losses ??
                      "–";
                    const goalsFor =
                      row.all?.goals?.for ?? row.goalsDiff?.for ?? "–";
                    const goalsAgainst =
                      row.all?.goals?.against ?? row.goalsDiff?.against ?? "–";
                    const goalDiff =
                      row.goalsDiff ??
                      (typeof goalsFor === "number" &&
                      typeof goalsAgainst === "number"
                        ? goalsFor - goalsAgainst
                        : "–");
                    const points = row.points ?? "–";

                    return (
                      <tr
                        className={`border-b border-white/[0.04] transition-colors ${
                          isThis ? "bg-blue-500/10" : "hover:bg-white/[0.02]"
                        }`}
                        key={tName || ri}
                        ref={isThis ? teamRowRef : undefined}
                        style={
                          isThis
                            ? { borderLeft: `4px solid ${teamColors.primary}` }
                            : undefined
                        }
                      >
                        <td className="p-3 text-white/40 text-xs">{rank}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {(row.team?.logo || row.logo) && (
                              <img
                                alt=""
                                className="w-5 h-5 object-contain"
                                src={row.team?.logo || row.logo}
                              />
                            )}
                            <span
                              className={
                                isThis
                                  ? "text-white font-bold"
                                  : "text-white/80"
                              }
                            >
                              {tName}
                            </span>
                          </div>
                        </td>
                        <td className="text-center p-3 text-white/40 text-xs">
                          {played}
                        </td>
                        <td className="text-center p-3 text-white/60 text-xs">
                          {wins}
                        </td>
                        <td className="text-center p-3 text-white/60 text-xs">
                          {draws}
                        </td>
                        <td className="text-center p-3 text-white/60 text-xs">
                          {losses}
                        </td>
                        <td className="text-center p-3 text-white/40 text-xs hidden md:table-cell">
                          {goalsFor}
                        </td>
                        <td className="text-center p-3 text-white/40 text-xs hidden md:table-cell">
                          {goalsAgainst}
                        </td>
                        <td className="text-center p-3 text-white/40 text-xs hidden md:table-cell">
                          {typeof goalDiff === "number"
                            ? goalDiff > 0
                              ? `+${goalDiff}`
                              : goalDiff
                            : goalDiff}
                        </td>
                        <td className="text-center p-3 text-white font-bold text-xs">
                          {points}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TeamDetailStandingsTab;
